<template>
	<template v-if="isInitialLoader">
		<AnimationScanTicket v-if="route.path.includes('scan')" />
		<AnimationNumbersSelect v-else />
	</template>

	<main v-else>
		<ResultHero />
		<TabsResults />
		<ModalAnalyzePerfomance v-if="$hasFeature(REQUIRE_LOGIN_FOR_ANALYZE_PERFORMANCE)" />
		<ModalDetail v-else />
		<ModalAuth
			v-model="resultStore.showModalSignUp"
			@close-auth-google="closeAuthGoogle"
		>
			<template #header>
				<div class="space-y-4 text-center">
					<div class="w-16 h-16 mx-auto rounded-full bg-[#18212F] flex items-center justify-center">
						<UIcon
							name="i-mono-icons:unlock"
							class="size-10 text-primary-400 scale-x-[-1]"
						/>
					</div>
					<div class="text-neutral-50 space-y-2.5 text-center">
						<h1 class="text-xl sm:text-3xl font-bold">{{ $t('Unlock this Result') }}</h1>
						<p
							class="text-base text-neutral-50"
							v-html="textHtml"
						></p>
					</div>
				</div>
			</template>
		</ModalAuth>
		<ModalPrizeBreakdown v-if="$hasFeature(UNLOCK_LOTTERY_RESULTS)" />
		<ModalDreams />
	</main>
</template>

<script setup lang="ts">
import {
	SHOW_EXTERNAL_LINK,
	REQUIRE_LOGIN_FOR_ANALYZE_PERFORMANCE,
	UNLOCK_LOTTERY_RESULTS,
} from '~/config/featureFlags'
import ResultHero from '~/components/pages/results/Header/ResultHero.vue'
import TabsResults from '~/components/pages/results/TabsResults.vue'
import ModalDetail from '~/components/pages/results/modals/ModalDetail.vue'
import ModalAnalyzePerfomance from '~/components/pages/results/modals/ModalAnalyzePerfomance.vue'
import ModalDreams from '~/components/pages/results/modals/ModalDreams.vue'
import { useResultStore } from '~/store/result'
import { filteredQueryParams } from '~/utils/filteredQueryParams'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import { trackPurchaseView } from '~/tracking/resultEvents'
import AnimationNumbersSelect from '~/components/pages/results/AnimationNumbersSelect.vue'
import AnimationScanTicket from '~/components/pages/results/AnimationScanTicket.vue'

import { toQueryString } from '~/utils/queryString'

const ModalAuth = defineAsyncComponent(() => import('~/components/modals/ModalAuth.vue'))
const ModalPrizeBreakdown = defineAsyncComponent(
	() => import('~/components/pages/results/modals/ModalPrizeBreakdown.vue'),
)

definePageMeta({ middleware: ['result'] })

const resultStore = useResultStore()
const route = useRoute()
const { $hasFeature: _$hasFeature } = useNuxtApp()
const $hasFeature = _$hasFeature ?? (() => false)
const { t: $t } = useI18n()
const resultsParams = useResultsParams()

const firstLoad = ref(true)

const withMinDuration = async (fn: () => Promise<void>, minMs = 250) => {
	const start = Date.now()
	await fn()
	const elapsed = Date.now() - start
	if (elapsed < minMs) await new Promise((r) => setTimeout(r, minMs - elapsed))
}

const stripUiQuery = (q: Record<string, any>) => {
	const copy = { ...q }
	delete copy.modal
	delete copy.modal_id
	delete copy.modal_tab
	// query params tab lottery results
	delete copy.lottery_id_tab_lottery_results
	delete copy.start_date
	delete copy.end_date
	delete copy.page_number_tab_lottery_results
	return copy
}

const cleanQuery = computed(() => stripUiQuery(route.query as any))
const cleanKey = computed(() => JSON.stringify(cleanQuery.value))

const resetFetchedData = () => {
	resultStore.SET_STATE_RESULT({ type: 'lotteries', value: null })
	resultStore.SET_STATE_RESULT({ type: 'analytics', value: null })
	resultStore.SET_STATE_RESULT({ type: 'pagination', value: null })
	resultStore.SET_STATE_RESULT({ type: 'prizes', value: null })
	resultStore.SET_STATE_RESULT({ type: 'lottery_id', value: null })
	resultStore.SET_STATE_RESULT({ type: 'loading', value: false })
}

const { pending, error } = useAsyncData(
	() => `result:${cleanKey.value}`,
	async () => {
		if (!resultsParams.value.hasSufficientParams) {
			resetFetchedData()
			if (firstLoad.value) firstLoad.value = false
			return null
		}

		try {
			await withMinDuration(() => fetchData(cleanQuery.value), 250)
		} finally {
			// ✅ al terminar la primera carga, nunca más mostramos el full loader
			if (firstLoad.value) firstLoad.value = false
		}
	},
	{
		watch: [cleanKey, () => resultsParams.value.hasSufficientParams],
		lazy: true,
		server: false,
	},
)

const isInitialLoader = computed(() => firstLoad.value && pending.value)

const textHtml = computed(() => {
	return $t('View full {lottery} analysis and statistics for free.', {
		lottery: `<span class="font-semibold text-primary-500">${$t(resultStore.getLotterySelect?.name || '')}</span>`,
	})
})

const fetchData = async (query: any) => {
	const queryParams = toQueryString(filteredQueryParams(query))
	if (IDS_LOTTERIES_CLASSIC.includes(Number(query.lottery_id))) {
		return await resultStore.fetchPrizesClassic(queryParams)
	}
	return await resultStore.fetchPrizes(queryParams)
}

const closeAuthGoogle = () => {
	resultStore.SET_STATE_RESULT({
		type: 'showModalSignUp',
		value: false,
	})
}

const eventPurchaseView = () => {
	if (!$hasFeature(SHOW_EXTERNAL_LINK)) return
	if (route.name !== 'results') return
	trackPurchaseView()
}

watch(
	() => resultsParams.value.hasSufficientParams,
	(isValid) => {
		if (isValid) return
		resetFetchedData()
		if (firstLoad.value) firstLoad.value = false
	},
	{ immediate: true },
)

watch(
	() => pending.value,
	(p) => {
		if (!p && import.meta.client && !error.value && resultsParams.value.hasSufficientParams) eventPurchaseView()
	},
	{ immediate: true },
)

watch(
	() => isInitialLoader.value,
	(loading) => {
		const isScanPage = /\/results\/scan(?:\/|$)/.test(route.path)

		if (loading && import.meta.client && !isScanPage) {
			window.scrollTo({ top: 0, behavior: 'instant' })
		}
	},
	{ immediate: true },
)
</script>

<style src="~/assets/css/animations.css"></style>
